import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Breadcrumbs, Container, Eyebrow, H1, H2, H3, Section, Spinner, EmptyState,
} from "../components/Primitives";
import { createBooking, getService, getSlots, apiError } from "../lib/api";
import { GuaranteeLine } from "../components/GuaranteeLine";
import { useCurrency } from "../lib/currency";
import { useSEO } from "../lib/seo";
import { AnimatePresence, EASE_OUT, motion, stepSwap } from "../lib/motion";

const STEPS = [
  "Choose a slot",
  "Choose your consultant",
  "Your details",
  "Birth details",
  "Scope of practice",
  "Pay",
];

const TIMEZONES = [
  "Asia/Kolkata", "Europe/London", "America/New_York", "America/Chicago", "America/Los_Angeles",
  "Asia/Dubai", "America/Toronto", "Australia/Sydney", "Asia/Singapore", "Europe/Berlin", "UTC",
];

export const CRISIS_LINES = [
  ["Tele-MANAS", "Ministry of Health & Family Welfare · national · 24×7 · multiple languages", "14416 or 1-800-891-4416"],
  ["KIRAN", "Ministry of Social Justice & Empowerment · 24×7 · 13 languages", "1800-599-0019"],
  ["Emergency services", "Police, fire and ambulance", "112"],
];

export function CrisisPanel({ compact = false }) {
  return (
    <div className="border-l-2 border-l-alert bg-surface p-5" data-testid="crisis-panel">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-alert" aria-hidden="true" />
        <p className="ui text-sm font-semibold text-ink">If you are in crisis</p>
      </div>
      <p className="ui mt-2 text-sm text-dim">
        If you are having thoughts of harming yourself or someone else, or you are in immediate danger,
        please do not use this service. Contact one of the following now.
      </p>
      <ul className="mt-4 space-y-2">
        {CRISIS_LINES.map(([name, desc, number]) => (
          <li key={name} className="ui flex flex-wrap items-baseline justify-between gap-2 border-b border-rule pb-2 text-sm">
            <span>
              <strong className="text-ink">{name}</strong>
              {!compact && <span className="block text-xs text-dim">{desc}</span>}
            </span>
            <a href={`tel:${number.split(" ")[0]}`} className="num text-alert">{number}</a>
          </li>
        ))}
      </ul>
      <p className="ui mt-3 text-xs text-dim">
        Or go to the nearest hospital emergency department. If you are outside India, please dial your
        country's emergency number. Our consultants are not trained in crisis intervention, and this
        service cannot provide emergency help.
      </p>
    </div>
  );
}

function StepBar({ step }) {
  return (
    <div className="mb-10" data-testid="booking-steps">
      <div className="relative h-px w-full bg-rule">
        <motion.span
          className="absolute inset-y-0 left-0 block bg-gold"
          initial={false}
          animate={{ width: `${(step / (STEPS.length - 1)) * 100}%` }}
          transition={{ duration: 0.55, ease: EASE_OUT }}
        />
      </div>
      <ol className="no-scrollbar mt-5 flex gap-1 overflow-x-auto">
        {STEPS.map((label, i) => (
          <li key={label} className="flex shrink-0 items-center gap-2 pr-5">
            <span
              className={`num flex h-6 w-6 items-center justify-center border text-xs transition-colors duration-300 ${
                i < step ? "border-verify bg-verify text-white"
                  : i === step ? "border-gold text-gold" : "border-rule text-dim"
              }`}
            >
              {i < step ? <Check className="h-3 w-3" /> : i + 1}
            </span>
            <span className={`ui text-xs transition-colors duration-300 ${i === step ? "text-gold" : "text-dim"}`}>
              {label}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function Booking() {
  const { service: slug } = useParams();
  const { format } = useCurrency();
  const [step, setStep] = useState(0);
  const [tz, setTz] = useState(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata"; }
    catch (e) { return "Asia/Kolkata"; }
  });
  const [slot, setSlot] = useState(null);
  const [consultant, setConsultant] = useState(null);
  const [details, setDetails] = useState({ name: "", email: "", phone: "", question: "" });
  const [birth, setBirth] = useState({ birth_name: "", birth_date: "", birth_time: "", birth_place: "", gender: "", gotra: "" });
  const [consents, setConsents] = useState({ chart_preparation: false, recording: false, marketing: false });
  const [scope, setScope] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const svc = useQuery({ queryKey: ["service", slug], queryFn: () => getService(slug) });
  const slots = useQuery({
    queryKey: ["slots", slug, consultant],
    queryFn: () => getSlots({ service: slug, ...(consultant ? { consultant } : {}) }),
    enabled: Boolean(slug),
  });

  const s = svc.data?.service;
  useSEO({
    title: s ? `Book ${s.branded_name} — ${s.seo_subtitle}` : "Book a consultation",
    description: "Timezone-aware booking. One flat price, tax inclusive. No wallet, no per-minute billing, no upsell at the payment step.",
    noindex: true,
  });

  const grouped = useMemo(() => {
    const out = {};
    (slots.data?.slots || []).forEach((x) => {
      const d = new Date(x.start_utc).toLocaleDateString("en-CA", { timeZone: tz });
      out[d] = out[d] || [];
      out[d].push(x);
    });
    return Object.entries(out).slice(0, 10);
  }, [slots.data, tz]);

  const fmtTime = (iso) =>
    new Date(iso).toLocaleTimeString([], { timeZone: tz, hour: "2-digit", minute: "2-digit" });

  const submit = async () => {
    setBusy(true);
    try {
      const r = await createBooking({
        service_slug: slug,
        consultant_slug: consultant || svc.data.consultants[0].slug,
        slot_start_utc: slot.start_utc,
        customer_timezone: tz,
        ...details,
        currency: "INR",
        ...birth,
        consents: Object.entries(consents).map(([purpose, granted]) => ({ purpose, granted })),
        scope_of_practice_accepted: scope,
      });
      setConfirmed(r);
      toast.success("Booked. Confirmation and calendar invite on their way.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadIcs = () => {
    const blob = new Blob([confirmed.ics], { type: "text/calendar;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${confirmed.booking.booking_ref}.ics`;
    a.click();
  };

  if (svc.isLoading) return <Container className="py-20"><Spinner /></Container>;
  if (svc.isError || !s) {
    return (
      <Container className="py-20">
        <EmptyState title="No such consultation">
          <Link to="/consult" className="link-u">See all consultations →</Link>
        </EmptyState>
      </Container>
    );
  }

  if (confirmed) {
    const b = confirmed.booking;
    return (
      <Section>
        <Container className="max-w-3xl">
          <Eyebrow>Confirmed</Eyebrow>
          <H1 className="mt-4">{b.service_name} with {b.consultant_name}</H1>
          <p className="num mt-4 text-lg text-ink" data-testid="booking-when">
            {new Date(b.slot_start_utc).toLocaleString([], {
              timeZone: b.customer_timezone, weekday: "long", day: "numeric", month: "long",
              hour: "2-digit", minute: "2-digit",
            })}{" "}
            ({b.customer_timezone})
          </p>
          <p className="num mt-2 text-sm text-dim">
            Reference <span data-testid="booking-ref">{b.booking_ref}</span> · {b.duration_minutes} minutes ·{" "}
            {format(b.price_inr)} paid in full
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button type="button" onClick={downloadIcs} className="btn btn-primary" data-testid="download-ics">
              Download calendar invite (.ics)
            </button>
            <a href={b.video_link} className="btn btn-secondary">Your video link</a>
          </div>

          <div className="card-s mt-10 p-6">
            <H2 className="h2-sub">What happens next, and when</H2>
            <ul className="mt-4 divide-y divide-rule">
              {confirmed.sequence.map((x) => (
                <li key={x.when} className="ui flex justify-between gap-4 py-2.5 text-sm">
                  <span className="num shrink-0 text-gold">{x.when}</span>
                  <span className="text-right text-dim">{x.what}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="card-s mt-6 p-6">
            <H3>Reschedule and cancellation, in plain words</H3>
            <p className="ui mt-2 text-sm text-dim">
              Free cancellation or reschedule up to 24 hours before your slot. Inside 24 hours, one free
              reschedule. If your consultant fails to attend, or the call drops or fails to connect, you
              get a full refund and a free rebooking within 24 hours — processed automatically, without
              you asking and without you proving anything.
            </p>
          </div>

          <div className="mt-6"><CrisisPanel /></div>
        </Container>
      </Section>
    );
  }

  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs trail={[
          { label: "Home", to: "/" }, { label: "Consult", to: "/consult" },
          { label: s.branded_name, to: `/consult/${s.slug}` }, { label: "Book" },
        ]} />
      </Container>

      <Section className="pb-20">
        <Container className="max-w-3xl">
          <Eyebrow>{s.seo_subtitle}</Eyebrow>
          <H1 className="mt-4">Book {s.branded_name}</H1>
          <p className="num mt-3 text-sm text-dim">
            {format(s.price_inr)} · {s.duration_minutes >= 240 ? "four 60-minute sessions" : `${s.duration_minutes} minutes`} · flat fee, tax inclusive
          </p>

          <div className="mt-10"><StepBar step={step} /></div>

          <AnimatePresence mode="wait">
            <motion.div key={step} variants={stepSwap} initial="hidden" animate="show" exit="exit">

          {step === 0 && (
            <div data-testid="step-slot">
              <H2>Choose a slot</H2>
              <label className="ui mt-4 flex flex-wrap items-center gap-2 text-sm text-dim">
                All times shown in
                <select
                  value={tz}
                  onChange={(e) => setTz(e.target.value)}
                  data-testid="timezone-select"
                  className="ui border border-rule bg-transparent px-2 py-1 text-sm text-ink"
                >
                  {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                — change it if that's wrong.
              </label>
              {slots.isLoading ? (
                <div className="mt-6 space-y-6" aria-busy="true" data-testid="slots-skeleton">
                  {Array.from({ length: 2 }).map((_, d) => (
                    <div key={d}>
                      <div className="skeleton h-4 w-44" />
                      <div className="mt-3 flex flex-wrap gap-2">
                        {Array.from({ length: 6 }).map((_, s) => (
                          <div key={s} className="skeleton h-9 w-20" />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : grouped.length === 0 ? (
                <div className="mt-6" data-testid="slots-empty">
                  <EmptyState title="No open slots in this window">
                    Every listed time is taken right now. New weeks open as each consultant publishes
                    their diary — check back tomorrow, or{" "}
                    <Link to="/contact" className="link-u">write to us</Link> and we will hold the
                    next opening for you.
                  </EmptyState>
                </div>
              ) : (
                <div className="mt-6 space-y-6">
                  {grouped.map(([day, list]) => (
                    <div key={day}>
                      <p className="num text-sm text-ink">
                        {new Date(`${day}T12:00:00`).toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" })}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {list.map((x) => (
                          <button
                            key={x.start_utc}
                            type="button"
                            disabled={!x.available}
                            onClick={() => { setSlot(x); setConsultant(x.consultant); setStep(1); }}
                            data-testid={`slot-${x.start_utc}`}
                            className={`num border px-3 py-2 text-sm transition-colors ${
                              x.available
                                ? "border-rule text-ink hover:border-gold hover:text-gold"
                                : "border-rule/40 bg-wash/40 text-dim/45 cursor-not-allowed"
                            }`}
                            aria-disabled={!x.available}
                            title={x.available ? undefined : "Not available — pick another time"}
                          >
                            {fmtTime(x.start_utc)}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="ui mt-6 text-xs text-dim">{slots.data?.note}</p>
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-consultant">
              <H2>Choose your consultant</H2>
              <p className="ui mt-2 text-sm text-dim">
                Real names, real training lineage, no ratings theatre. Whoever you pick earns nothing from
                anything you buy afterwards.
              </p>
              <ul className="mt-6 space-y-4">
                {(svc.data.consultants || []).map((c) => (
                  <li key={c.slug}>
                    <button
                      type="button"
                      onClick={() => { setConsultant(c.slug); setStep(2); }}
                      data-testid={`consultant-${c.slug}`}
                      className={`card-s flex w-full gap-5 p-5 text-left transition-colors hover:border-gold ${
                        consultant === c.slug ? "border-gold" : ""
                      }`}
                    >
                      <img src={c.photo} alt={c.display_name} className="h-24 w-24 shrink-0 object-cover" />
                      <div>
                        <H3>{c.display_name}</H3>
                        <p className="num mt-1 text-xs text-dim">
                          {c.years_practising} years · {c.languages.join(", ")}
                        </p>
                        <p className="ui mt-2 text-sm text-dim">{c.bio}</p>
                        <p className="ui mt-2 text-xs text-dim">{c.training_lineage}</p>
                        <p className="ui mt-2 text-xs text-verify">
                          Holds {c.sessions_per_week} sessions a week · commission rate 0
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
              <button type="button" onClick={() => setStep(0)} className="link-u ui mt-6 text-sm">← Back to slots</button>
            </div>
          )}

          {step === 2 && (
            <form
              data-testid="step-details"
              onSubmit={(e) => { e.preventDefault(); setStep(3); }}
              className="space-y-4"
            >
              <H2>Your details and your question</H2>
              <label className="block">
                <span className="field-label">Your name</span>
                <input required value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} className="field" data-testid="booking-name" />
              </label>
              <label className="block">
                <span className="field-label">Email</span>
                <input required type="email" value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} className="field" data-testid="booking-email" />
              </label>
              <label className="block">
                <span className="field-label">Phone (with country code)</span>
                <input required value={details.phone} onChange={(e) => setDetails({ ...details, phone: e.target.value })} className="field num" data-testid="booking-phone" />
              </label>
              <label className="block">
                <span className="field-label">The specific thing you want to discuss</span>
                <textarea required rows={4} value={details.question} onChange={(e) => setDetails({ ...details, question: e.target.value })} className="field" data-testid="booking-question" />
              </label>
              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(1)} className="btn btn-secondary">Back</button>
                <button type="submit" className="btn btn-primary" data-testid="details-next">Continue</button>
              </div>
            </form>
          )}

          {step === 3 && (
            <form
              data-testid="step-birth"
              onSubmit={(e) => {
                e.preventDefault();
                if (!consents.chart_preparation) {
                  toast.error("We cannot prepare a chart without consent for chart preparation.");
                  return;
                }
                setStep(4);
              }}
              className="space-y-4"
            >
              <H2>Birth details</H2>
              <p className="ui text-sm text-dim">
                Used solely to prepare your chart. Never sold, never shared with a second consultant,
                never used for advertising or model training.{" "}
                <Link to="/legal/consent-notice" className="link-u">Read the consent notice →</Link>
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="field-label">Name on the chart</span>
                  <input required value={birth.birth_name} onChange={(e) => setBirth({ ...birth, birth_name: e.target.value })} className="field" data-testid="birth-name" />
                </label>
                <label className="block">
                  <span className="field-label">Date of birth</span>
                  <input required type="date" value={birth.birth_date} onChange={(e) => setBirth({ ...birth, birth_date: e.target.value })} className="field num" data-testid="birth-date" />
                </label>
                <label className="block">
                  <span className="field-label">Exact time of birth</span>
                  <input required type="time" value={birth.birth_time} onChange={(e) => setBirth({ ...birth, birth_time: e.target.value })} className="field num" data-testid="birth-time" />
                </label>
                <label className="block">
                  <span className="field-label">Place of birth</span>
                  <input required value={birth.birth_place} onChange={(e) => setBirth({ ...birth, birth_place: e.target.value })} className="field" data-testid="birth-place" />
                </label>
                <label className="block">
                  <span className="field-label">Gender</span>
                  <select value={birth.gender} onChange={(e) => setBirth({ ...birth, gender: e.target.value })} className="field ui">
                    <option value="">Prefer not to say</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <label className="block">
                  <span className="field-label">Gotra (optional)</span>
                  <input value={birth.gotra} onChange={(e) => setBirth({ ...birth, gotra: e.target.value })} className="field" />
                </label>
              </div>

              <fieldset className="card-s mt-6 space-y-4 p-5">
                <legend className="eyebrow px-1">Itemised consent — nothing here is pre-ticked</legend>
                {[
                  ["chart_preparation", "Yes — use my birth date, time and place solely to prepare my chart and conduct this consultation."],
                  ["recording", "Yes — record the session and send me the recording afterwards. (Optional. The session goes ahead either way.)"],
                  ["marketing", "Yes — email me guidance and offers. (Optional. Nothing else changes if you say no.)"],
                ].map(([key, label]) => (
                  <label key={key} htmlFor={`consent-${key}`} className="flex cursor-pointer items-start gap-3">
                    <input
                      id={`consent-${key}`}
                      type="checkbox"
                      checked={consents[key]}
                      onChange={(e) => setConsents({ ...consents, [key]: e.target.checked })}
                      data-testid={`consent-${key}`}
                      className="mt-1 h-4 w-4 shrink-0 accent-[var(--c-gold)]"
                    />
                    <span className="ui text-sm text-dim">{label}</span>
                  </label>
                ))}
                <p className="ui text-xs text-dim">
                  You can say yes to some and no to others. Withdrawal is as easy as consent — one click
                  at <Link to="/account/data" className="link-u">/account/data</Link>, and we confirm
                  deletion by email.
                </p>
              </fieldset>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(2)} className="btn btn-secondary">Back</button>
                <button type="submit" className="btn btn-primary" data-testid="birth-next">Continue</button>
              </div>
            </form>
          )}

          {step === 4 && (
            <div data-testid="step-scope">
              <H2>Scope of practice</H2>
              <div className="card-s mt-4 p-5">
                <p className="ui text-sm text-dim">
                  What we provide is interpretive guidance drawn from traditional Vedic astrology,
                  numerology and allied traditional disciplines, offered as a framework for personal
                  reflection and decision-making. We do not provide any medical, psychiatric,
                  psychological, clinical or therapeutic service. We do not diagnose, assess, treat or
                  manage any illness, disorder or condition. We do not provide legal, financial, tax or
                  investment advice. No outcome is guaranteed.{" "}
                  <Link to="/legal/scope-of-practice" className="link-u">Read it in full →</Link>
                </p>
              </div>

              <label htmlFor="scope-accept" className="mt-5 flex cursor-pointer items-start gap-3">
                <input
                  id="scope-accept"
                  type="checkbox"
                  checked={scope}
                  onChange={(e) => setScope(e.target.checked)}
                  data-testid="scope-accept"
                  className="mt-1 h-4 w-4 shrink-0 accent-[var(--c-gold)]"
                />
                <span className="ui text-sm text-dim">
                  I have read the Scope of Practice and understand this is not a medical, psychiatric or
                  psychological service, and that no outcome is guaranteed.
                </span>
              </label>

              <div className="mt-8"><CrisisPanel /></div>

              <div className="mt-8 flex gap-3">
                <button type="button" onClick={() => setStep(3)} className="btn btn-secondary">Back</button>
                <button
                  type="button"
                  disabled={!scope}
                  onClick={() => setStep(5)}
                  className="btn btn-primary disabled:cursor-not-allowed disabled:opacity-40"
                  data-testid="scope-next"
                >
                  Continue to payment
                </button>
              </div>
            </div>
          )}

          {step === 5 && (
            <div data-testid="step-pay">
              <H2>Pay</H2>
              <div className="card-s mt-4 p-6">
                <dl className="space-y-3 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="ui text-dim">{s.branded_name}</dt>
                    <dd className="num text-ink">{format(s.price_inr)}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="ui text-dim">Duration</dt>
                    <dd className="num text-ink">
                      {s.duration_minutes >= 240 ? "4 × 60 minutes" : `${s.duration_minutes} minutes`}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="ui text-dim">SAC code · GST</dt>
                    <dd className="num text-ink">{s.sac_code} · {s.gst_rate}% (included)</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="ui text-dim">When</dt>
                    <dd className="num text-right text-ink">
                      {slot && new Date(slot.start_utc).toLocaleString([], {
                        timeZone: tz, day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                      })}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4 border-t border-rule pt-3">
                    <dt className="ui font-semibold text-ink">Total</dt>
                    <dd className="num text-lg font-semibold text-gold">{format(s.price_inr)}</dd>
                  </div>
                </dl>
                <p className="ui mt-4 text-xs text-dim">
                  One flat price, tax inclusive, in your currency. There is no wallet, no stored balance,
                  no per-minute billing and no upsell on this screen.
                </p>
                <p className="ui mt-2 text-xs text-saffron">
                  DEMO CHECKOUT — no payment provider is connected in this build. Confirming will create a
                  real booking record without taking money.
                </p>
                <button
                  type="button"
                  disabled={busy}
                  onClick={submit}
                  className="btn btn-primary mt-6 w-full"
                  data-testid="confirm-booking"
                >
                  {busy ? "Confirming…" : `Confirm and pay ${format(s.price_inr)}`}
                </button>
                <GuaranteeLine testId="booking-guarantee" className="mt-4">
                  {slug === "suitability-session"
                    ? "Credited back in full against any purchase over ₹5,000 within 30 days."
                    : "If a consultant sells you a product on the call, the session is refunded in full."}
                </GuaranteeLine>
              </div>
              <button type="button" onClick={() => setStep(4)} className="link-u ui mt-6 text-sm">← Back</button>
            </div>
          )}
            </motion.div>
          </AnimatePresence>
        </Container>
      </Section>
    </>
  );
}
